import styled, { css } from "styled-components";

import { IRideStatusIndicatorControl } from "@ryde/controls";
import { IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  greyMui,
  MOBILE_BREAKPOINT,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

import {
  HEADER_STATION_NAME_COLOR,
  HEADER_STATION_NAME_FONT_WEIGHT,
  HEADER_STATION_NAME_HOVER_COLOR,
} from "../../header-station-name.constants";

const STATION_ROUTE_HEADER_STATION_NAME_MAX_WIDTH_PX = 138;

const headerInfoLineOverflowCss = css`
  min-width: 0;
  overflow: hidden;
  width: 100%;

  > * {
    flex-shrink: 0;
  }

  > *:has(.item__content) {
    min-width: 0;
    flex-shrink: 1;
    overflow: hidden;
  }

  .item__content {
    display: flex;
    align-items: center;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
  }
`;

export const StationRouteHeaderStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "station-route-header",
})`
  position: sticky;
  top: 0;
  height: 66px;
  width: 100%;
  background-color: ${rydeColors.white};
  border-bottom: 1px solid ${greyMui._300};
  box-shadow: 0px 2px 3px rgba(172, 171, 171, 0.2);
  display: flex;
  align-items: center;
  padding-inline-start: 25px;
  min-width: 0;
  z-index: ${ScreenDepth.HEADER}; // make a sticky header on top of other content

  .StationRouteDrawer-HeaderTitle {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
  }

  .StationRouteDrawer-HeaderButtons {
    flex-shrink: 0;
  }

  transition:
    filter 0.55s ease-out,
    backdrop-filter 0.55s ease-out;
  &.inner-item-editing {
    filter: blur(2px) grayscale(100%);
    backdrop-filter: blur(3px);
    pointer-events: none;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-inline-start: 10px;
    font-size: 12px;
  }

  .station-route-header-customized-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-inline-start: 10px;
    padding: 2px 8px;
    border: 1px solid ${rydeColors.yellowButton};
    border-radius: 5px;
    color: ${rydeColors.yellowButton};
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
  }

  .station-route-header-customized-sparkle {
    flex-shrink: 0;
    font-size: 13px;
    line-height: 1;
  }
`;

export const HeaderLine1Styled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "station-route-header-line1",
})`
  height: 30px;
  display: flex;
  align-items: center;
  font-size: 18px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: 40px;
    font-size: 13px;
    line-height: 15px;
    color: ${greyMui._800};
    font-weight: 600;
  }
`;
export const HeaderLine2Styled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "station-route-header-line2",
})`
  ${headerInfoLineOverflowCss};
  height: 30px;
  display: flex;
  align-items: center;
  font-size: 12px;
  white-space: nowrap;

  .info {
    margin-inline-start: 3px;
  }

  .station-route-header-station {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .station-route-header-station .label {
    flex-shrink: 0;
  }

  .station-route-header-station-name {
    color: ${HEADER_STATION_NAME_COLOR};
    font-weight: ${HEADER_STATION_NAME_FONT_WEIGHT};
    margin-inline-start: 5px;
  }

  .station-route-header-station-name.pointer {
    cursor: pointer;

    &:hover {
      color: ${HEADER_STATION_NAME_HOVER_COLOR};
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid ${blueGreyMui._400};
      outline-offset: 1px;
    }
  }

  .station-route-header-station .truncate-text {
    flex: 1 1 0%;
    max-width: ${STATION_ROUTE_HEADER_STATION_NAME_MAX_WIDTH_PX}px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .station-route-header-map-provider .info {
    margin-inline-start: 2px;
  }

  & > :not(:last-child)::after {
    content: "";
    border-inline-end: 1px solid ${blueGreyMui._300};
    height: 8px;
    margin-inline-start: 8px;
    display: inline-block;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    height: auto;
    flex-flow: wrap;
    margin-top: 4px;
    padding-inline-start: 8px;
    width: 100%;
  }
`;

export const StationRouteHeaderStatusStyled = styled.label.attrs<IStyledAttributes>(
  {
    "data-e2e": "station-route-header-status",
  },
)<IRideStatusIndicatorControl>`
  color: 2px solid ${props => props.$leftColor};
`;

export const StationRouteHeaderPriceDropdownStyled = styled.div.attrs<IStyledAttributes>(
  {
    "data-e2e": "station-route-header-price-dropdown",
  },
)`
  width: 226px;
  border: 1px solid ${blueGreyMui.A100};
  padding: 12px;
  border-radius: 5px;
  .headline {
    color: ${blueGreyMui._500};
    display: flex;
    align-items: center;
    margin-bottom: 17px;
    font-size: 13px;
    font-weight: 600;
    .linkOut {
      cursor: pointer;
      margin-inline-start: auto;
    }
  }
  .price_row {
    font-size: 14px;
    display: flex;
    align-items: center;
    margin-bottom: 17px;
    font-weight: 400;
    .price {
      margin-inline-start: auto;
    }
  }
  .total_row {
    font-size: 14px;
    display: flex;
    align-items: center;
    font-weight: 700;
    .total {
      margin-inline-start: auto;
    }
  }
`;
